import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { PageHeader, SectionTitle, Card, Badge } from '../design-system';
import UserProfileContext from '../context/userprofilecontext.js';
import {
  listProspects,
  getProspect,
  patchProspectStage,
  patchProspectDemo,
  postProspectNote,
  postProspectInvite,
} from '../api/prospects';
import { formatDate } from '../utils/formatters';
import styles from './page-prospects.module.css';

// ── Constants ─────────────────────────────────────────────────────────────────

const STAGE_ORDER = ['NEW', 'CONTACTED', 'QUALIFIED', 'OPPORTUNITY', 'SIGNED_UP', 'WON', 'DISQUALIFIED', 'LOST'];
const ACTIVE_STAGES = ['NEW', 'CONTACTED', 'QUALIFIED', 'OPPORTUNITY', 'SIGNED_UP', 'WON'];
const TERMINAL_STAGES = ['DISQUALIFIED', 'LOST'];
const ALL_STAGES = [...ACTIVE_STAGES, ...TERMINAL_STAGES];

const PERSONA_LABELS = {
  consumer: 'Consumer',
  professional: 'Professional',
  unknown: 'Unknown',
};

const SOURCE_LABELS = {
  form_demo: 'Demo Form',
  form_contact: 'Contact Form',
  purchased: 'Purchased',
  referral: 'Referral',
  manual: 'Manual',
  chat: 'Chat',
};

const ACTIVITY_LABELS = {
  FORM_SUBMITTED: 'Form submitted',
  EMAIL_SENT: 'Email sent',
  EMAIL_REPLIED: 'Email replied',
  EMAIL_BOUNCED: 'Email bounced',
  CHAT_STARTED: 'Chat started',
  CHAT_QUALIFIED: 'Chat qualified',
  DEMO_COMPLETED: 'Demo completed',
  STAGE_CHANGED: 'Stage changed',
  NOTE_ADDED: 'Note added',
  ESCALATED: 'Escalated',
  SIGNED_UP: 'Signed up',
  WON: 'Won',
};

/** Map stage → Badge variant */
function stageBadgeVariant(stage) {
  switch (stage) {
    case 'NEW':          return 'default';
    case 'CONTACTED':    return 'info';
    case 'QUALIFIED':    return 'accent';
    case 'OPPORTUNITY':  return 'warning';
    case 'SIGNED_UP':    return 'success';
    case 'WON':          return 'success';
    case 'DISQUALIFIED': return 'error';
    case 'LOST':         return 'error';
    default:             return 'default';
  }
}

/** Map activity type → Badge variant */
function activityBadgeVariant(type) {
  switch (type) {
    case 'WON':
    case 'SIGNED_UP':
    case 'DEMO_COMPLETED': return 'success';
    case 'EMAIL_BOUNCED':  return 'error';
    case 'STAGE_CHANGED':  return 'accent';
    case 'NOTE_ADDED':     return 'default';
    default:               return 'info';
  }
}

/** Format a prospect's display name */
function prospectDisplayName(prospect) {
  const first = prospect.firstName || '';
  const last = prospect.lastName || '';
  const name = `${first} ${last}`.trim();
  return name || prospect.email || '—';
}

// ── Stat row ──────────────────────────────────────────────────────────────────

function StageStats({ prospects }) {
  const counts = ALL_STAGES.reduce((acc, s) => ({ ...acc, [s]: 0 }), {});
  prospects.forEach((p) => {
    if (counts[p.stage] !== undefined) counts[p.stage]++;
  });

  const groups = [
    { label: 'Total', value: prospects.length, highlight: false },
    { label: 'New', value: counts['NEW'], highlight: false },
    { label: 'Qualified', value: counts['QUALIFIED'] + counts['OPPORTUNITY'], highlight: false },
    { label: 'Won', value: counts['WON'], highlight: true },
  ];

  return (
    <div className={styles.statsRow}>
      {groups.map((g) => (
        <Card key={g.label} padding="md" className={styles.statCard}>
          <div className={`${styles.statValue} ${g.highlight ? styles.statHighlight : ''}`}>
            {g.value}
          </div>
          <div className={styles.statLabel}>{g.label}</div>
        </Card>
      ))}
    </div>
  );
}

// ── Filter bar ────────────────────────────────────────────────────────────────

function FilterBar({ filters, onChange }) {
  const { selectedStages, selectedPersonas, selectedSources } = filters;

  function toggleStage(stage) {
    const next = selectedStages.includes(stage)
      ? selectedStages.filter((s) => s !== stage)
      : [...selectedStages, stage];
    onChange({ ...filters, selectedStages: next });
  }

  function togglePersona(p) {
    const next = selectedPersonas.includes(p)
      ? selectedPersonas.filter((x) => x !== p)
      : [...selectedPersonas, p];
    onChange({ ...filters, selectedPersonas: next });
  }

  function toggleSource(s) {
    const next = selectedSources.includes(s)
      ? selectedSources.filter((x) => x !== s)
      : [...selectedSources, s];
    onChange({ ...filters, selectedSources: next });
  }

  function clearAll() {
    onChange({ selectedStages: [], selectedPersonas: [], selectedSources: [] });
  }

  const hasFilters = selectedStages.length > 0 || selectedPersonas.length > 0 || selectedSources.length > 0;

  return (
    <Card padding="sm" className={styles.filterBar}>
      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Stage</span>
        <div className={styles.filterChips}>
          {ALL_STAGES.map((stage) => (
            <button
              key={stage}
              type="button"
              aria-pressed={selectedStages.includes(stage)}
              className={`${styles.filterChip} ${selectedStages.includes(stage) ? styles.filterChipActive : ''}`}
              onClick={() => toggleStage(stage)}
            >
              {stage}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Persona</span>
        <div className={styles.filterChips}>
          {Object.entries(PERSONA_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={selectedPersonas.includes(key)}
              className={`${styles.filterChip} ${selectedPersonas.includes(key) ? styles.filterChipActive : ''}`}
              onClick={() => togglePersona(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>Source</span>
        <div className={styles.filterChips}>
          {Object.entries(SOURCE_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={selectedSources.includes(key)}
              className={`${styles.filterChip} ${selectedSources.includes(key) ? styles.filterChipActive : ''}`}
              onClick={() => toggleSource(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {hasFilters && (
        <button type="button" className={styles.clearBtn} onClick={clearAll}>
          Clear filters
        </button>
      )}
    </Card>
  );
}

// ── Prospect table ────────────────────────────────────────────────────────────

function ProspectTable({ prospects, onSelect }) {
  if (prospects.length === 0) {
    return (
      <Card padding="md" className={styles.emptyState}>
        <p className={styles.emptyText}>No prospects match your filters.</p>
      </Card>
    );
  }

  return (
    <Card padding="none" className={styles.tableCard}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Name</th>
            <th className={styles.th}>Email</th>
            <th className={styles.th}>Stage</th>
            <th className={styles.th}>Persona</th>
            <th className={styles.th}>Source</th>
            <th className={styles.th}>Demo</th>
            <th className={styles.th}>Created</th>
          </tr>
        </thead>
        <tbody>
          {prospects.map((prospect) => (
            <tr
              key={prospect.prospectID}
              className={styles.tableRow}
              onClick={() => onSelect(prospect)}
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(prospect); }}
              aria-label={`View prospect ${prospectDisplayName(prospect)}`}
            >
              <td className={styles.td}>
                <span className={styles.nameCell}>{prospectDisplayName(prospect)}</span>
              </td>
              <td className={styles.td}>
                <span className={styles.emailCell}>{prospect.email}</span>
              </td>
              <td className={styles.td}>
                <Badge variant={stageBadgeVariant(prospect.stage)} size="sm">
                  {prospect.stage}
                </Badge>
              </td>
              <td className={styles.td}>
                {PERSONA_LABELS[prospect.persona] || prospect.persona || '—'}
              </td>
              <td className={styles.td}>
                {SOURCE_LABELS[prospect.source] || prospect.source || '—'}
              </td>
              <td className={styles.td}>
                {prospect.demo_completed ? (
                  <Badge variant="success" size="sm">Done</Badge>
                ) : (
                  <span className={styles.demoPending}>—</span>
                )}
              </td>
              <td className={styles.td}>
                <span className={styles.dateCell}>
                  {prospect.createDate ? formatDate(prospect.createDate) : '—'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ── Prospect detail drawer ────────────────────────────────────────────────────

function ProspectDrawer({ prospect, onClose, onUpdated }) {
  const [detail, setDetail] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [detailError, setDetailError] = useState(null);

  const [selectedStage, setSelectedStage] = useState('');
  const [savingStage, setSavingStage] = useState(false);
  const [stageError, setStageError] = useState(null);

  const [markingDemo, setMarkingDemo] = useState(false);
  const [demoError, setDemoError] = useState(null);

  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState(null);

  const [inviting, setInviting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState(null);

  const drawerRef = useRef(null);

  // Load full detail + activities when drawer opens
  useEffect(() => {
    if (!prospect) return;
    setLoadingDetail(true);
    setDetailError(null);
    setDetail(null);
    setActivities([]);
    setSelectedStage(prospect.stage || '');

    getProspect(prospect.prospectID)
      .then((data) => {
        setDetail(data.prospect);
        setActivities(data.activities || []);
        setSelectedStage(data.prospect?.stage || '');
      })
      .catch((err) => {
        console.error('Failed to load prospect detail:', err);
        setDetailError('Failed to load prospect details.');
      })
      .finally(() => setLoadingDetail(false));
  }, [prospect]);

  // Focus trap: close on Escape
  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  async function handleSaveStage() {
    if (!selectedStage || selectedStage === detail?.stage) return;
    setSavingStage(true);
    setStageError(null);
    try {
      const result = await patchProspectStage(detail.prospectID, selectedStage);
      const updated = result.prospect;
      setDetail(updated);
      setSelectedStage(updated.stage);
      // Refresh activities to show STAGE_CHANGED entry
      const refreshed = await getProspect(updated.prospectID);
      setActivities(refreshed.activities || []);
      onUpdated(updated);
    } catch (err) {
      console.error('Failed to update stage:', err);
      setStageError('Failed to save stage change.');
    } finally {
      setSavingStage(false);
    }
  }

  async function handleMarkDemo() {
    if (detail?.demo_completed) return;
    setMarkingDemo(true);
    setDemoError(null);
    try {
      const result = await patchProspectDemo(detail.prospectID);
      const updated = result.prospect;
      setDetail(updated);
      const refreshed = await getProspect(updated.prospectID);
      setActivities(refreshed.activities || []);
      onUpdated(updated);
    } catch (err) {
      console.error('Failed to mark demo:', err);
      setDemoError('Failed to mark demo completed.');
    } finally {
      setMarkingDemo(false);
    }
  }

  async function handleAddNote(e) {
    e.preventDefault();
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    setNoteError(null);
    try {
      await postProspectNote(detail.prospectID, text);
      setNoteText('');
      const refreshed = await getProspect(detail.prospectID);
      setActivities(refreshed.activities || []);
    } catch (err) {
      console.error('Failed to add note:', err);
      setNoteError('Failed to add note.');
    } finally {
      setSavingNote(false);
    }
  }

  async function handleSendInvite() {
    setInviting(true);
    setInviteMessage(null);
    try {
      await postProspectInvite(detail.prospectID);
      setInviteMessage('Invite sent successfully.');
    } catch (err) {
      console.error('Failed to send invite:', err);
      setInviteMessage('Failed to send invite — check logs.');
    } finally {
      setInviting(false);
    }
  }

  if (!prospect) return null;

  const current = detail || prospect;
  const stageDirty = selectedStage && selectedStage !== current.stage;

  return (
    <>
      {/* Overlay */}
      <div
        className={styles.drawerOverlay}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        ref={drawerRef}
        className={styles.drawer}
        aria-label="Prospect detail"
        role="complementary"
      >
        {/* Header */}
        <div className={styles.drawerHeader}>
          <div>
            <h2 className={styles.drawerName}>{prospectDisplayName(current)}</h2>
            <p className={styles.drawerEmail}>{current.email}</p>
          </div>
          <div className={styles.drawerHeaderRight}>
            <Badge variant={stageBadgeVariant(current.stage)} size="md">
              {current.stage}
            </Badge>
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Close prospect detail"
            >
              ✕
            </button>
          </div>
        </div>

        {loadingDetail ? (
          <div className={styles.drawerLoading}>Loading…</div>
        ) : detailError ? (
          <div className={styles.drawerError}>{detailError}</div>
        ) : (
          <div className={styles.drawerBody}>

            {/* Quick facts */}
            <section className={styles.drawerSection}>
              <SectionTitle title="Details" />
              <div className={styles.factGrid}>
                <span className={styles.factLabel}>Persona</span>
                <span>{PERSONA_LABELS[current.persona] || current.persona || '—'}</span>
                <span className={styles.factLabel}>Source</span>
                <span>{SOURCE_LABELS[current.source] || current.source || '—'}</span>
                {current.company && (
                  <>
                    <span className={styles.factLabel}>Company</span>
                    <span>{current.company}</span>
                  </>
                )}
                {current.role && (
                  <>
                    <span className={styles.factLabel}>Role</span>
                    <span>{current.role}</span>
                  </>
                )}
                {current.phone && (
                  <>
                    <span className={styles.factLabel}>Phone</span>
                    <span>{current.phone}</span>
                  </>
                )}
                <span className={styles.factLabel}>Created</span>
                <span>{current.createDate ? formatDate(current.createDate) : '—'}</span>
                <span className={styles.factLabel}>Consent</span>
                <span>{current.consent_marketing ? 'Yes' : 'No'}</span>
              </div>
            </section>

            {/* Stage control */}
            <section className={styles.drawerSection}>
              <SectionTitle title="Change Stage" />
              <div className={styles.stageControl}>
                <select
                  className={styles.stageSelect}
                  value={selectedStage}
                  onChange={(e) => setSelectedStage(e.target.value)}
                  aria-label="Select new stage"
                >
                  {STAGE_ORDER.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className={styles.saveBtn}
                  disabled={!stageDirty || savingStage}
                  onClick={handleSaveStage}
                >
                  {savingStage ? 'Saving…' : 'Save'}
                </button>
              </div>
              {stageError && <p className={styles.errorText}>{stageError}</p>}
            </section>

            {/* Demo toggle */}
            <section className={styles.drawerSection}>
              <SectionTitle title="Demo" />
              {current.demo_completed ? (
                <p className={styles.demoStatus}>
                  Demo completed
                  {current.demo_completed_date
                    ? ` on ${formatDate(current.demo_completed_date)}`
                    : ''}.
                </p>
              ) : (
                <div>
                  <button
                    type="button"
                    className={styles.demoBtn}
                    disabled={markingDemo}
                    onClick={handleMarkDemo}
                  >
                    {markingDemo ? 'Saving…' : 'Mark demo completed'}
                  </button>
                  {demoError && <p className={styles.errorText}>{demoError}</p>}
                </div>
              )}
            </section>

            {/* Send invite */}
            <section className={styles.drawerSection}>
              <SectionTitle title="Invite" />
              <div className={styles.inviteRow}>
                <button
                  type="button"
                  className={styles.inviteBtn}
                  disabled={inviting}
                  onClick={handleSendInvite}
                >
                  {inviting ? 'Sending…' : 'Send signup invite'}
                </button>
                {inviteMessage && (
                  <span className={styles.inviteMessage}>{inviteMessage}</span>
                )}
              </div>
            </section>

            {/* Add note */}
            <section className={styles.drawerSection}>
              <SectionTitle title="Add Note" />
              <form onSubmit={handleAddNote} className={styles.noteForm}>
                <textarea
                  className={styles.noteInput}
                  rows={3}
                  maxLength={5000}
                  placeholder="Add a note about this prospect…"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  aria-label="Note text"
                />
                <button
                  type="submit"
                  className={styles.saveBtn}
                  disabled={!noteText.trim() || savingNote}
                >
                  {savingNote ? 'Saving…' : 'Add note'}
                </button>
                {noteError && <p className={styles.errorText}>{noteError}</p>}
              </form>
            </section>

            {/* Activity timeline */}
            <section className={styles.drawerSection}>
              <SectionTitle title={`Activity (${activities.length})`} />
              {activities.length === 0 ? (
                <p className={styles.emptyText}>No activity recorded yet.</p>
              ) : (
                <ol className={styles.timeline}>
                  {[...activities].reverse().map((act) => (
                    <li key={act.activityID} className={styles.timelineItem}>
                      <div className={styles.timelineDot} />
                      <div className={styles.timelineContent}>
                        <div className={styles.timelineHeader}>
                          <Badge variant={activityBadgeVariant(act.type)} size="sm">
                            {ACTIVITY_LABELS[act.type] || act.type}
                          </Badge>
                          <span className={styles.timelineDate}>
                            {act.timestamp ? formatDate(act.timestamp) : ''}
                          </span>
                        </div>
                        {act.summary && (
                          <p className={styles.timelineSummary}>{act.summary}</p>
                        )}
                        {act.fromStage && act.toStage && (
                          <p className={styles.timelineStageChange}>
                            {act.fromStage} → {act.toStage}
                          </p>
                        )}
                        {act.inquiryType && (
                          <p className={styles.timelineMeta}>Inquiry: {act.inquiryType}</p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

          </div>
        )}
      </aside>
    </>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function ProspectsPage() {
  const userProfileContext = useContext(UserProfileContext);
  const isSuperAdmin = userProfileContext?.userProfile?.isSuperAdmin;

  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [filters, setFilters] = useState({
    selectedStages: [],
    selectedPersonas: [],
    selectedSources: [],
  });

  const [selectedProspect, setSelectedProspect] = useState(null);

  const fetchProspects = useCallback(async (activeFilters) => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await listProspects({
        stages: activeFilters.selectedStages.length > 0 ? activeFilters.selectedStages : undefined,
        personas: activeFilters.selectedPersonas.length > 0 ? activeFilters.selectedPersonas : undefined,
        sources: activeFilters.selectedSources.length > 0 ? activeFilters.selectedSources : undefined,
      });
      setProspects(result.prospects || []);
    } catch (err) {
      console.error('Failed to load prospects:', err);
      setLoadError('Failed to load prospects. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (isSuperAdmin) {
      fetchProspects(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  function handleFiltersChange(newFilters) {
    setFilters(newFilters);
    fetchProspects(newFilters);
  }

  function handleProspectUpdated(updatedProspect) {
    setProspects((prev) =>
      prev.map((p) =>
        p.prospectID === updatedProspect.prospectID ? { ...p, ...updatedProspect } : p
      )
    );
    // Keep drawer open with updated data reflected via selectedProspect
    if (selectedProspect?.prospectID === updatedProspect.prospectID) {
      setSelectedProspect((prev) => ({ ...prev, ...updatedProspect }));
    }
  }

  // ── Guard ──────────────────────────────────────────────────────────────────

  if (!isSuperAdmin) {
    return (
      <div className={styles.page}>
        <PageHeader title="Prospects" subtitle="Pipeline & conversion tracking" />
        <Card padding="md" className={styles.accessDenied}>
          <p className={styles.accessDeniedText}>
            This page is restricted to super admins.
          </p>
        </Card>
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className={styles.page}>
      <PageHeader
        title="Prospects"
        subtitle={`Pipeline & conversion tracking${prospects.length > 0 ? ` · ${prospects.length} shown` : ''}`}
        actions={
          <button
            type="button"
            className={styles.refreshBtn}
            disabled={loading}
            onClick={() => fetchProspects(filters)}
            aria-label="Refresh prospect list"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        }
      />

      {loadError && (
        <div className={styles.errorBanner} role="alert">{loadError}</div>
      )}

      <StageStats prospects={prospects} />

      <section className={styles.section}>
        <SectionTitle title="Filters" />
        <FilterBar filters={filters} onChange={handleFiltersChange} />
      </section>

      <section className={styles.section}>
        <SectionTitle title="Prospects" />
        {loading && prospects.length === 0 ? (
          <Card padding="md" className={styles.emptyState}>
            <p className={styles.emptyText}>Loading…</p>
          </Card>
        ) : (
          <ProspectTable
            prospects={prospects}
            onSelect={setSelectedProspect}
          />
        )}
      </section>

      {selectedProspect && (
        <ProspectDrawer
          prospect={selectedProspect}
          onClose={() => setSelectedProspect(null)}
          onUpdated={handleProspectUpdated}
        />
      )}
    </div>
  );
}
